import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolWarning,
} from "../types";

/**
 * Ganirelix — the same job as cetrorelix, with none of the preparation.
 *
 * It is the pair to `cetrotide.ts` and it exists as its own protocol for one
 * reason: the device. Cetrorelix is a kit to mix; this arrives as a prefilled
 * syringe with the dose already in it. Same molecule class, same site, same
 * daily hour, and a procedure that is four sentences shorter.
 *
 * ### The sentence that makes this different from every protocol before it
 *
 * "Do NOT attempt to adjust the dose." It is the same instruction Ovidrel's
 * syringe carries, and it matters more here: this is a daily injection, she will
 * give it eight to ten times, and by the third morning she is not reading. A
 * plunger that looks adjustable is the one thing in this procedure a patient can
 * get wrong by being confident.
 *
 * ### Two sites, and she picks
 *
 * The content offers the lower abdomen "or upper thigh", and both are here. The
 * first draft kept only the abdomen because the camera guide can outline one and
 * not the other — which is the aid deciding the medicine. Over eight to ten
 * daily injections the thigh is the release valve for an abdomen that is bruised
 * or too tender, so the walk asks before she cleans anything, and the camera
 * then does whatever it honestly can at the site she chose.
 */

const ORGALUTRAN_BRANDS = [
  {
    name: "Orgalutran",
    markets: [
      "EU",
      "GB",
      "CH",
      "AU",
      "NZ",
      "CA",
      "JP",
      "MY",
      "SG",
      "TH",
      "HK",
      "ID",
      "VN",
      "IN",
      "MX",
    ],
  },
  {
    name: "Fyremadel",
    markets: ["GB", "BE", "DK", "FI", "FR", "DE", "IT", "ES", "US"],
  },
] as const satisfies readonly ProtocolBrand[];

/** The same two hours cetrorelix draws, from this medication's own content. */
const MISSED_DOSE: ProtocolWarning = {
  id: "orgalutran.missed-dose",
  severity: "caution",
  // `none`, and the sentence explains why: it is the warning itself that tells
  // her when to call, not the coach routing her. A caution never pauses the
  // walk, so an escalation declared on one would promise a destination the
  // session has no moment to reach — see `protocol-contract.test.ts`.
  escalation: "none",
  copy: {
    en: "If you miss a dose by less than 2 hours, inject immediately. If more than 2 hours late, contact your clinic.",
    fr: "Si le retard est inférieur à 2 heures, injectez immédiatement. Au-delà, contactez votre centre.",
  },
  sourceSegment:
    "⚠ Missed dose - If you miss a dose by less than 2 hours, inject immediately. If more than 2 hours late, contact your clinic.",
};

const WHEN_TO_CALL: ProtocolWarning = {
  id: "orgalutran.when-to-call",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Call your clinic for severe injection site reactions (spreading redness, warmth, pus) or unusual symptoms.",
    fr: "Contactez votre centre en cas de réaction sévère au point d'injection (rougeur qui s'étend, chaleur, écoulement purulent) ou de symptômes inhabituels.",
  },
  sourceSegment:
    "⚠ When to call - Call your clinic for severe injection site reactions (spreading redness, warmth, pus) or unusual symptoms.",
};

/**
 * The half of "when to call" that does not belong to a clinic.
 *
 * The content listed a spreading site reaction, an allergic reaction and
 * "unusual symptoms" in one sentence with one destination. Anaphylaxis is not a
 * phone call to a fertility clinic (Anaïs, 2026-09-22), so the content was
 * split before this restated it, and the two halves route where each belongs.
 */
const BREATHING_OR_ALLERGIC: ProtocolWarning = {
  id: "orgalutran.breathing-or-allergic-reaction",
  severity: "stop",
  escalation: "emergency",
  copy: {
    en: "Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
    fr: "Aide urgente - Des difficultés respiratoires ou des signes de réaction allergique nécessitent d'appeler les secours tout de suite.",
  },
  sourceSegment:
    "⚠ Seek urgent help - Difficulty breathing or signs of an allergic reaction need emergency services straight away.",
};

/**
 * The two situations the triggers already covered and these did not.
 *
 * Not because they are likely — this arrives prefilled, with nothing to mix and
 * nothing to transfer — but because a patient who has one of them should not
 * find out whether the app has an answer by having one (Anaïs, 2026-09-22). The same situation gets the same action
 * whichever medication she is holding; four procedures disagreeing about that
 * is the disagreement worth removing.
 *
 * Bloating matters most here of all. These are given through exactly the
 * stimulation days when OHSS develops, and until now the coach only mentioned
 * it on the trigger walk — days later, to the same patient.
 */
const SPILL_OR_INCOMPLETE_DOSE: ProtocolWarning = {
  id: "orgalutran.spill-or-incomplete-dose",
  severity: "stop",
  escalation: "clinic-soon",
  copy: {
    en: "Spill or incomplete dose - If you spill any medication or suspect an incomplete dose, call your clinic as soon as you can. Do not attempt a second injection without guidance.",
    fr: "Renversement ou dose incomplète - Si vous renversez du produit ou suspectez une dose incomplète, appelez votre centre dès que possible. Ne tentez pas une seconde injection sans avis médical.",
  },
  sourceSegment:
    "⚠ Spill or incomplete dose - If you spill any medication or suspect an incomplete dose, call your clinic as soon as you can. Do not attempt a second injection without guidance.",
};

const SEVERE_BLOATING: ProtocolWarning = {
  id: "orgalutran.severe-bloating",
  severity: "stop",
  escalation: "clinic-now",
  copy: {
    en: "Severe bloating - Call your clinic the same day for severe bloating.",
    fr: "Ballonnements sévères - Appelez votre centre le jour même en cas de ballonnements sévères.",
  },
  sourceSegment:
    "⚠ Severe bloating - Call your clinic the same day for severe bloating.",
};

export const ORGALUTRAN_PROTOCOL: InjectionProtocol = {
  medicationSlug: "orgalutran",
  brands: ORGALUTRAN_BRANDS,
  aliases: ["Ganirelix", "Ganirélix"],
  sites: [
    {
      id: "abdomen",
      label: {
        en: "Lower abdomen",
        fr: "Bas du ventre",
      },
      copy: {
        en: "Clean site - Clean the injection site on your lower abdomen (1-2 inches from the navel) with an alcohol swab and let it dry fully.",
        fr: "Désinfecter le site - Désinfectez le site sur le bas du ventre (à 2-5 cm du nombril) à l'alcool et laissez sécher complètement.",
      },
    },
    {
      id: "thigh",
      label: {
        en: "Upper thigh",
        fr: "Haut de la cuisse",
      },
      copy: {
        en: "Clean site - Clean the injection site on your upper thigh with an alcohol swab and let it dry fully. Find the area by placing one hand below your hip and the other above your knee, and inject between them, on the outer half of your thigh.",
        fr: "Désinfecter le site - Désinfectez le site sur le haut de la cuisse à l'alcool et laissez sécher complètement. Repérez la zone en posant une main sous la hanche et l'autre au-dessus du genou, et injectez entre les deux, sur la moitié externe de la cuisse.",
      },
    },
  ],
  indication: "antagonist",
  // One prefilled syringe, 0.25 mg in 0.5 mL, and the content says so in
  // capitals: the dose is never adjusted.
  dose: { unit: "mg", strengths: [0.25] },
  form: "pre_filled_syringe",
  prescriptionForms: ["pre_filled_syringe"],
  route: "subcutaneous",
  version: "2026-09-24.1",
  derivedFrom: "content/data/medications/gnrh-antagonists.json#ganirelix",
  steps: [
    {
      id: "orgalutran.syringe.prepare",
      kind: "prepare",
      requiresConfirmation: false,
      // The fridge first and the hands second, which is the order the content's
      // two sentences describe rather than the order it prints them in: the
      // syringe needs a minute or two to warm, and that is a minute she can
      // spend washing her hands instead of waiting.
      copy: {
        en: "Prepare - Remove the prefilled syringe from the refrigerator and let it warm to room temperature for 1-2 minutes. Wash hands thoroughly. No mixing required - it is ready to inject.",
        fr: "Préparer - Sortez la seringue préremplie du réfrigérateur et laissez-la atteindre la température ambiante 1 à 2 minutes. Lavez-vous soigneusement les mains. Aucune préparation nécessaire - elle est prête à injecter.",
      },
      warnings: [MISSED_DOSE],
    },
    {
      id: "orgalutran.syringe.ready-the-syringe",
      kind: "verify",
      requiresConfirmation: true,
      copy: {
        en: "Check and prepare - Remove the needle cap carefully without touching the needle. Check that the solution is clear and colorless - do not use if cloudy or particles are visible. Do NOT attempt to adjust the dose. If there is a visible air bubble, hold the syringe needle-up and tap gently, but do not expel liquid.",
        fr: "Vérifier et préparer - Retirez prudemment le capuchon de l'aiguille sans la toucher. Vérifiez que la solution est claire et incolore - ne pas utiliser si trouble ou particulaire. Ne PAS tenter d'ajuster la dose. En présence d'une bulle d'air, tenez la seringue aiguille vers le haut et tapotez doucement, sans chasser de liquide.",
      },
    },
    {
      id: "orgalutran.syringe.clean-site",
      kind: "prepare",
      requiresConfirmation: false,
      copy: {
        en: "Clean site - Clean the injection site on your lower abdomen (1-2 inches from the navel) with an alcohol swab and let it dry fully.",
        fr: "Désinfecter le site - Désinfectez le site sur le bas du ventre (à 2-5 cm du nombril) à l'alcool et laissez sécher complètement.",
      },
    },
    {
      id: "orgalutran.syringe.inject",
      kind: "irreversible",
      requiresConfirmation: true,
      copy: {
        en: "Inject - Pinch the skin firmly, insert the needle at a 90° angle, and depress the plunger slowly and steadily until the syringe is completely empty. Hold for 5 seconds before withdrawing to ensure the full dose is delivered. Continue daily through trigger day (inclusive).",
        fr: "Injecter - Pincez fermement la peau, insérez l'aiguille à 90° et poussez le piston lentement et régulièrement jusqu'à ce que la seringue soit complètement vide. Maintenez 5 secondes avant de retirer pour garantir la délivrance complète. Continuez quotidiennement jusqu'au jour du déclencheur inclus.",
      },
      warnings: [
        WHEN_TO_CALL,
        BREATHING_OR_ALLERGIC,
        SPILL_OR_INCOMPLETE_DOSE,
        SEVERE_BLOATING,
      ],
    },
    {
      id: "orgalutran.syringe.aftercare",
      kind: "aftercare",
      requiresConfirmation: false,
      copy: {
        en: "After injection - Apply gentle pressure with clean gauze - do not rub. Rotate injection sites daily (left side, then right). Dispose of the used syringe in a sharps container - each syringe is single-use only.",
        fr: "Après l'injection - Appliquez une légère pression avec une compresse propre - sans frotter. Alternez les sites chaque jour (côté gauche, puis droit). Jetez la seringue usagée dans un collecteur - à usage unique.",
      },
    },
  ],
};
